/**
 * pdfExportConfig — PDF-6.9b PDF Options sub-dialog backing config (pure).
 *
 * AutoCAD's Page Setup dialog has a "PDF Options" / "PLOT options" sub-modal
 * that captures the PDF-specific knobs that have no AcDbPlotSettings home:
 * vector quality (DPI), raster image quality, merge/transparency, custom
 * paper sizes, layer info / OCG, hyperlinks, bookmarks, font handling. This
 * module is the PURE typed model for the SUBSET those knobs cover today:
 *
 *   - `vectorDpi` — vector quality (tessellation tolerance). Defaults to 1200
 *     dpi (AutoCAD's PDF "vector quality" default; see Autodesk: Plot Setup
 *     Dialog Box / PDF Options "Vector resolution"). The exporter consumes a
 *     `toleranceMm` tessellation knob (mm-per-segment for arcs / circles /
 *     curves); {@link dpiToToleranceMm} maps the user-facing DPI to that
 *     internal knob (half-pixel at the chosen print resolution).
 *   - `fontMode` — font handling. Today only one option: TTF embed (the
 *     existing PDF text path). PDFSHX (searchable invisible-text overlay for
 *     stroke SHX glyphs) is NOT shipped: there is no backend for it yet, so
 *     gating UI on backend existence (Codex P2 rule) means the dropdown has a
 *     single option — kept as a union so a future slice grows the enum
 *     without changing the surface.
 *   - `omitLayers` — layer NAMES the user unchecked in the "Include Layers"
 *     panel. The exporter SKIPS these names in its OCG layer-batch loop, so
 *     plotted geometry on an omitted layer does not appear in the output PDF.
 *     Defaults to `[]` (every layer included).
 *   - `includeBookmarks` — multi-sheet PUBLISH outline tree. Default ON;
 *     setting it OFF skips the `/Outlines` build in `publishLayouts`.
 *
 * # Out of scope (NOT shipped, NOT faked)
 *
 *   - Raster DPI (deferred until PDF-6.5 gradient / raster fallback ships).
 *   - PDFSHX / searchable SHX (deferred — no invisible-text-overlay backend).
 *   - Hyperlinks (deferred PDF-4b — no `/Link` annotation emit).
 *
 * # Shipped in PDF-6.4
 *
 *   - `mergeControl` — "Lines merge" parity for AutoCAD's PC3 setting. Default
 *     `"overwrite"` (top objects hide bottom — PDF Normal blend); `"merge"`
 *     wraps every painted primitive in `/BM /Multiply` so overlaps darken.
 *
 * Per CLAUDE.md PRIORITY #1: do not add a UI control whose backend is absent.
 */

import type { PlotStyleTable } from "@dxf-ai-editor/acadctb";

/**
 * Font handling for PDF text. Today one option (the existing TTF embed path).
 * Kept as a union so a future PDFSHX (searchable invisible-text overlay) slice
 * grows the enum without renaming the field; renderers / hooks switch on the
 * literal.
 */
export type PdfFontMode = "ttf-embed";

/**
 * PDF-6.4 — AutoCAD "Lines merge / Overwrite" PC3 plot setting (parity).
 *
 *   - `"overwrite"` — top objects hide bottom (PDF Normal blend). This is the
 *     pre-6.4 behaviour and the default for fresh configs.
 *   - `"merge"` — overlapping geometry darkens (PDF `/BM /Multiply` ExtGState
 *     wrapped around every painted primitive). Matches AutoCAD's "Lines merge"
 *     Plot setting.
 */
export type PdfMergeControl = "overwrite" | "merge";

/**
 * Loaded plot-style table descriptor (PDF-6.3b). The PDF Options dialog reads
 * a `.ctb` / `.stb` file, parses it via `@dxf-ai-editor/acadctb.parseAcadctb`,
 * and stores BOTH the parsed table AND the source filename so the dialog can
 * render a "loaded <kind> · <N> entries · <file>" summary + a Clear button.
 *
 * The filename is for display only — the resolver only ever consumes `table`.
 */
export interface PdfPlotStyleSelection {
  /** The decoded plot-style table (CTB or STB). */
  table: PlotStyleTable;
  /** Source filename (basename) for the dialog summary line. */
  fileName: string;
}

/**
 * The set of PDF-export options the "PDF Options" sub-dialog edits.
 *
 * READ NON-REACTIVELY at print-time by `usePrintDialog.onConfirm` and the
 * PUBLISH path in `SpaceTabsBar` via `usePdfOptionsDialog.getConfig()`; the
 * dialog's committed `config` is the live source of truth (not the in-flight
 * `draft`).
 */
export interface PdfExportConfig {
  /**
   * Vector quality, dots per inch. AutoCAD parity default = 1200 dpi.
   * Threaded into the exporter via {@link dpiToToleranceMm} → `toleranceMm`
   * (half-pixel at the chosen print resolution).
   */
  vectorDpi: number;
  /** Font handling. Today: TTF embed only (see {@link PdfFontMode}). */
  fontMode: PdfFontMode;
  /**
   * Layer NAMES the user unchecked in the "Include Layers" panel. The
   * exporter's OCG layer-batch loop SKIPS these names (no marked-content
   * wrapper, no `/OC` reference, no geometry emitted). Defaults to `[]`.
   *
   * Order is stable but not semantically meaningful; lookups go via
   * `new Set(omitLayers).has(name)` at print-time.
   */
  omitLayers: ReadonlyArray<string>;
  /**
   * Whether the multi-sheet PUBLISH should build a `/Outlines` (bookmark)
   * tree, one item per sheet. Default ON. When OFF, `publishLayouts` skips
   * the outline build entirely (no `/Outlines` catalog entry).
   *
   * Has NO effect on a single-sheet PLOT (`exportLayoutPdf` never emits
   * bookmarks; bookmarks are a PUBLISH feature).
   */
  includeBookmarks: boolean;
  /**
   * PDF-6.3b — loaded CTB / STB plot-style table descriptor. Undefined when
   * the user has not loaded a table; the exporter then applies no overrides
   * (every entity plots with its authored colour / lineweight / linetype /
   * fully opaque, as in PDF-6.3a).
   *
   * The dialog uses `<input type="file" accept=".ctb,.stb">` → ArrayBuffer →
   * Uint8Array → `parseAcadctb` to populate this field, and stores the source
   * filename alongside the parsed table for the dialog's summary line.
   */
  plotStyleTable?: PdfPlotStyleSelection;
  /**
   * PDF-6.4 — AutoCAD "Lines merge / Overwrite" PC3 plot setting (parity).
   * Threaded session-level (NOT per-LAYOUT-persist — the `PlotLayoutFlags`
   * bitfield has no Merge-Control bit, and AutoCAD's PC3 file is a session /
   * output-device choice, not a per-layout setting).
   *
   * Default `"overwrite"` (the pre-6.4 behaviour); `"merge"` wraps every
   * painted primitive in `/BM /Multiply` so overlapping geometry darkens.
   */
  mergeControl: PdfMergeControl;
}

/**
 * Canonical defaults for a fresh PDF Options config: AutoCAD-parity 1200 dpi
 * vector quality, TTF embed text, no omitted layers, bookmarks ON.
 *
 * Returns a fresh object each call so callers may mutate / spread without
 * leaking into the next call's defaults.
 */
export function defaultPdfExportConfig(): PdfExportConfig {
  return {
    vectorDpi: 1200,
    fontMode: "ttf-embed",
    omitLayers: [],
    includeBookmarks: true,
    // PDF-6.4: default "Overwrite" preserves the pre-6.4 behaviour (PDF Normal
    // blend mode, top objects hide bottom). Users opt into Multiply via the
    // PDF Options dialog (the "Lines merge" radio).
    mergeControl: "overwrite",
  };
}

/**
 * Convert a vector-quality DPI to the exporter's `toleranceMm` tessellation
 * knob (mm per chord segment for arcs / circles / curves).
 *
 * Reasoning: at `D` dpi the printer's smallest addressable feature is
 * `25.4 / D` mm; tessellating to half that (the Nyquist-style "half-pixel"
 * rule of thumb) keeps the curve indistinguishable from a true arc at the
 * print resolution. So `tolMm = 25.4 / (2 * dpi)`.
 *
 * Sanity values (matching the dialog presets):
 *   - 600  dpi → 25.4 / 1200 ≈ 0.0211667 mm
 *   - 1200 dpi → 25.4 / 2400 ≈ 0.0105833 mm
 *   - 2400 dpi → 25.4 / 4800 ≈ 0.0052917 mm
 *   - 4800 dpi → 25.4 / 9600 ≈ 0.0026458 mm
 *
 * Guards: a non-positive / non-finite DPI falls back to `DEFAULT_PAPER_TOLERANCE_MM`-
 * equivalent (the existing 0.1 mm exporter default, ≈127 dpi) so a malformed
 * input cannot crash the export. The dialog itself constrains DPI to
 * 50..9600, but the helper is defensive.
 */
export function dpiToToleranceMm(dpi: number): number {
  if (!Number.isFinite(dpi) || dpi <= 0) return 0.1;
  return 25.4 / (2 * dpi);
}

/**
 * The DPI presets the dialog offers as fixed options, plus the "Custom"
 * entry. Exported so the dialog + tests share one source of truth.
 */
export const PDF_VECTOR_DPI_PRESETS: ReadonlyArray<number> = [600, 1200, 2400, 4800];

/** Hard min/max bounds for the Custom DPI input. */
export const PDF_VECTOR_DPI_MIN = 50;
export const PDF_VECTOR_DPI_MAX = 9600;
